import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MenuHeader from './MenuHeader';
import btnInfo from "../assets/images/icon-info.svg";
import Modal from "react-modal";
import goback from '../assets/images/goback.svg';
import useAxiosPrivate from "../hooks/useApi";
import DomainService from '../services/domain.service';
import CategoryService from '../services/category.service';
import ProductService from '../services/product.service';
import Nav from './Nav';
import { useNotifications } from '../context/NotificationProvider';
import useAuth from '../hooks/useAuth';

import remove from '../assets/images/remove.png';
import people from '../assets/images/people.png';
import { Button, Container, createTheme, IconButton, ThemeProvider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTranslatableData } from '../hooks/useTranslatable';
import { usePrompt } from '../hooks/usePrompt';

const EDIT_STEP_PATHS = [
  '/choose-products',
  '/choose-flypros'
];

const customStyles = {
  content: {
    width: "33%",
    top: "50%",
    padding: "40px",
    left: "50%",
    right: "auto",
    bottom: "auto",
    borderRadius: "10px",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
    maxWidth: "450px",
    margin: "auto",
    textAlign: "center",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
  },
};

const theme = createTheme({
  palette: {
    white: {
      "main": "#FFFFFF",
      "light": "#FFFFFF",
      "dark": "#FFFFFF",
      "contrastText": "#FFFFFF"
    },
    dark_yellow: {
      "main": "#393D44",
      "light": "#393D44",
      "dark": "#393D44",
      "contrastText": "#FAEF9B",
    },
    dark_white: {
      "main": "#393D44",
      "light": "#393D44",
      "dark": "#393D44",
      "contrastText": "#FFFFFF",
    },
    dark: {
      "main": "#393D44",
      "light": "#393D44",
      "dark": "#393D44",
      "contrastText": "#393D44"
    }
  },
});

const normalizeDomain = (domain) => {
  if (Array.isArray(domain)) {
    return domain.find((d) => d && d.id) || null;
  }
  return domain || null;
};

const normalizeSelectedServices = (services) => (
  (services || []).map((s) => ({
    ...s,
    domain: normalizeDomain(s.domain),
  }))
);

const hydrateSelectedServices = async (
  selectedServicesRaw,
  axiosPrivate,
  setSelectedServices,
  fetchCategories,
  fetchProducts
) => {
  const normalizeDomain = (domain) => {
    if (Array.isArray(domain)) {
      if (domain.length === 1) return domain[0];
      return domain.find(d => d && d.id) || domain[0] || null;
    }
    return domain || null;
  };

  const hydrated = await Promise.all(selectedServicesRaw.map(async (s) => {
    let domainObj = normalizeDomain(s.domain);
    let categoryObj = s.category || null;
    let subCategoryObj = s.subCategory || null;
    let categories = [];
    let sub_categories = [];
    let products = [];
    let productsCat = [];
    let cat_has_products = false;
    let sub_cat_has_products = false;
    let cat_has_subcategories = false;
    let showProductsForType = null;
    let showProductsForId = null;
    let showProductsForIdParent = null;

    if (domainObj) {
      categories = await fetchCategories(domainObj.id);
    }

    // If editing a booking with a subcategory
    if (categoryObj && categoryObj.parent_id) {
      // Fetch siblings (subcategories under the same parent)
      sub_categories = await CategoryService.index(
        { parent_id: categoryObj.parent_id, is_active: 1, with: 'media' },
        axiosPrivate
      );
      cat_has_subcategories = true;
      // Find and select parent category
      const parentCat = categories.find(c => c.id === categoryObj.parent_id);
      categoryObj = parentCat || categoryObj;
      subCategoryObj = s.category;
      // Fetch products of this subcategory
      products = await fetchProducts(subCategoryObj.id);
      sub_cat_has_products = products.length > 0;
      showProductsForType = "subCategory";
      showProductsForId = subCategoryObj.id;
      showProductsForIdParent = categoryObj.id;
    } else if (categoryObj && !categoryObj.parent_id) {
      // Standard case, no subcategory
      sub_categories = await CategoryService.index(
        { parent_id: categoryObj.id, is_active: 1, with: 'media' },
        axiosPrivate
      );
      cat_has_subcategories = sub_categories.length > 0;
      products = await fetchProducts(categoryObj.id);
      cat_has_products = products.length > 0;
      if (cat_has_products) {
        showProductsForType = "category";
        showProductsForId = categoryObj.id;
      }
    }

    // If subcategory is set, fetch parent's products as well
    if (subCategoryObj && categoryObj) {
      productsCat = await fetchProducts(categoryObj.id);
    }

    return {
      ...s,
      domain: domainObj,
      categories,
      category: categoryObj,
      sub_categories,
      subCategory: subCategoryObj,
      products,
      productsCat,
      cat_has_products,
      cat_has_subcategories,
      sub_cat_has_products,
      showProductsForType,
      showProductsForId,
      showProductsForIdParent,
    };
  }));
  setSelectedServices(hydrated);
};

function ChooseProducts() {
  const { t, i18n } = useTranslation('service');
  const [domains, setDomains] = useState([]);
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const notify = useNotifications();
  const { auth } = useAuth();

  const location = useLocation();
  const [state, setState] = useState(location.state || JSON.parse(sessionStorage.getItem('state') ?? '{}'));

  usePrompt(
    sessionStorage.getItem('editBookingMode') === 'true',
    "Êtes-vous sûr de vouloir quitter la modification ? Vos changements ne seront pas enregistrés.",
    next => EDIT_STEP_PATHS.some(p => next.pathname.startsWith(p))
  );

  const hotel = auth.user?.hotel?.[0] || {};
  const duo_capability =
    typeof hotel.duo_capability === 'boolean'
      ? hotel.duo_capability
      : Boolean(hotel?.duo_capability);
  const [isActive, setIsActive] = useState(state.duo || false);
  const [isRoom, setIsRoom] = useState(state.room ? state.room === 'SPA_ROOM' ? false : true : false);
  const fallbackDefault = [
    { domain: null, category: null, subCategory: null, product: null, cat_has_products: false, categories: [], sub_categories: [], products: [], sub_cat_has_products: false, cat_has_subcategories: false, showDescriptionForId: null, showDescriptionForType: null, showProductsForId: null, showProductsForType: null }
  ];
  const stateFromSession = JSON.parse(sessionStorage.getItem('state') || '{}');
  let initialSelectedServices = (
    location.state && Array.isArray(location.state.selectedServices) && location.state.selectedServices.length > 0
      ? location.state.selectedServices
      : (stateFromSession.selectedServices && Array.isArray(stateFromSession.selectedServices) && stateFromSession.selectedServices.length > 0
        ? stateFromSession.selectedServices
        : fallbackDefault)
  );

  initialSelectedServices = normalizeSelectedServices(initialSelectedServices);

  if (!Array.isArray(initialSelectedServices) || initialSelectedServices.length === 0) {
    initialSelectedServices = fallbackDefault;
  }
  const [selectedServices, setSelectedServices] = useState(initialSelectedServices);

  const isEditMode =
    state?.editBookingMode ||
    state?.modificationBookingId ||
    sessionStorage.getItem('editBookingMode') === 'true' ||
    sessionStorage.getItem('modificationBookingId');

  // Use translated data for display
  const translatedDomains = useTranslatableData(domains);
  const translatedSelectedServices = useTranslatableData(selectedServices);
  const [modalIsOpenService, setIsOpenService] = useState(false);
  const closeModalservice = () => setIsOpenService(false);
  const openModalservice = () => setIsOpenService(true);
  const [hasModalShown, setHasModalShown] = useState(false);

  const fetchAllDomains = async () => {
    try {
      const response = await DomainService.index(
        { hotel_id: hotel.id, with: 'media,zones', sort: 'ordering' },
        axiosPrivate
      );
      setDomains(response);
    } catch (error) {
      console.error("Error fetching domains:", error);
    }
  };

  const fetchCategories = async (domainId) => {
    try {
      const response = await CategoryService.index(
        { domain_id: domainId, parent_id: null, is_active: 1, with: 'media,subcategories' },
        axiosPrivate
      );
      return response;
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
    return [];
  };

  const fetchProducts = async (catId) => {
    try {
      const response = await ProductService.index(
        { category_id: catId, with: 'media, bookings', is_active: 1 },
        axiosPrivate
      );
      return response;
    } catch (error) {
      console.error("Error fetching products:", error);
    }
    return [];
  };

  useEffect(() => {
    fetchAllDomains();
    let prefill = null;
    if (
      location.state &&
      Array.isArray(location.state.selectedServices) &&
      location.state.selectedServices.length > 0
    ) {
      prefill = location.state.selectedServices;
    } else if (
      stateFromSession.selectedServices &&
      Array.isArray(stateFromSession.selectedServices) &&
      stateFromSession.selectedServices.length > 0
    ) {
      prefill = stateFromSession.selectedServices;
    }
    if (Array.isArray(prefill) && prefill.length > 0) {
      (async () => {
        await hydrateSelectedServices(
          prefill,
          axiosPrivate,
          setSelectedServices,
          fetchCategories,
          fetchProducts
        );
      })();
    }
    // Optionally set DUO mode as well
    let prefillDuo = location.state?.duo ?? stateFromSession?.duo;
    if (prefillDuo !== undefined) setIsActive(prefillDuo);

    // Defensive, always ensure selectedServices is an array
    if (!Array.isArray(selectedServices)) {
      setSelectedServices(fallbackDefault);
    }
    // eslint-disable-next-line
  }, []);

  const toggleSwitch = () => {
    setIsActive(!isActive);
  };

  const toggleRoomSwitch = () => {
    setIsRoom(!isRoom);
  };

  // When select a domain
  const handleDomainSelect = async (personIndex, domainObj) => {
    const updated = [...selectedServices];

    const categories = await fetchCategories(domainObj.id);

    updated[personIndex] = {
      domain: domainObj,
      category: null,
      subCategory: null,
      product: null,
      categories: categories,
      sub_categories: [],
      products: [],
      cat_has_products: false,
      sub_cat_has_products: false,
      cat_has_subcategories: false,
      showDescriptionForId: null,
      showDescriptionForType: null,
      showProductsForId: null,
      showProductsForIdParent: null,
      showProductsForType: null
    };

    setSelectedServices(updated);
  };

  const handleCategorySelect = async (personIndex, categoryObj) => {
    const updated = [...selectedServices];
    const currentlySelected = updated[personIndex].category?.id === categoryObj.id;

    if (currentlySelected) {
      updated[personIndex].category = null;
      updated[personIndex].subCategory = null;
      updated[personIndex].cat_has_products = false;
      updated[personIndex].sub_categories = [];
      updated[personIndex].sub_cat_has_products = false;
      updated[personIndex].showDescriptionForId = null;
      updated[personIndex].showDescriptionForType = null;
      updated[personIndex].showProductsForType = null;
      updated[personIndex].showProductsForId = null;
      updated[personIndex].products = [];
      updated[personIndex].product = null;
      updated[personIndex].cat_has_subcategories = false;

      setSelectedServices(updated);
      return;
    }

    updated[personIndex].category = categoryObj;
    updated[personIndex].subCategory = null;
    updated[personIndex].product = null;
    updated[personIndex].showDescriptionForId = null;
    updated[personIndex].showDescriptionForType = null;

    // Fetch Products and Subcategories
    const [products, subcats] = await Promise.all([
      fetchProducts(categoryObj.id),
      CategoryService.index(
        { parent_id: categoryObj.id, is_active: 1, with: 'media' },
        axiosPrivate
      )
    ]);

    updated[personIndex].products = products;
    updated[personIndex].sub_categories = subcats;
    updated[personIndex].cat_has_products = products.length > 0;
    updated[personIndex].cat_has_subcategories = subcats.length > 0;

    if (products.length > 0) {
      updated[personIndex].showProductsForType = "category";
      updated[personIndex].showProductsForId = categoryObj.id;
      updated[personIndex].showProductsForIdParent = null;
    } else {
      updated[personIndex].showProductsForType = null;
      updated[personIndex].showProductsForId = null;
      updated[personIndex].showProductsForIdParent = null;
    }

    setSelectedServices(updated);
  };

  // When select a subcategory
  const handleSubCategorySelect = async (personIndex, subcatObj) => {
    const updated = [...selectedServices];
    const currentlySelected = updated[personIndex].subCategory?.id === subcatObj.id;

    if (currentlySelected) {
      // Deselect the subcategory
      updated[personIndex].subCategory = null;
      updated[personIndex].product = null;
      updated[personIndex].sub_cat_has_products = false;
      updated[personIndex].showDescriptionForId = null;
      updated[personIndex].showDescriptionForType = null;
      updated[personIndex].showProductsForType = null;
      updated[personIndex].showProductsForId = null;
      updated[personIndex].products = [];

      setSelectedServices(updated);
      return;
    }

    updated[personIndex].subCategory = subcatObj;
    updated[personIndex].product = null;
    updated[personIndex].showDescriptionForId = null;
    updated[personIndex].showDescriptionForType = null;

    // Fetch Products under the selected subcategory and parent category
    const [products, products_parent] = await Promise.all([
      fetchProducts(subcatObj.id),
      fetchProducts(updated[personIndex].category.id)
    ]);

    updated[personIndex].products = products;
    updated[personIndex].productsCat = products_parent;
    updated[personIndex].sub_cat_has_products = products.length > 0;

    if (products.length > 0) {
      updated[personIndex].showProductsForType = "subCategory";
      updated[personIndex].showProductsForId = subcatObj.id;
      updated[personIndex].showProductsForIdParent = subcatObj.parent_id;
    } else {
      updated[personIndex].showProductsForType = null;
      updated[personIndex].showProductsForId = null;
      updated[personIndex].showProductsForIdParent = subcatObj.parent_id;
    }

    setSelectedServices(updated);
  };

  const handleProductSelect = (personIndex, prodObj) => {
    const updated = [...selectedServices];
    updated[personIndex].product = prodObj;
    setSelectedServices(updated);
  };

  // Add or remove person
  const addPerson = () => {
    if (selectedServices.length < 4) {
      setSelectedServices([
        ...selectedServices,
        { domain: null, category: null, subCategory: null, product: null, cat_has_products: false, categories: [], sub_categories: [], products: [], sub_cat_has_products: false, cat_has_subcategories: false, showDescriptionForId: null, showDescriptionForType: null, showProductsForId: null, showProductsForType: null }
      ]);
    }
  };

  const removePerson = (idx) => {
    setSelectedServices(selectedServices.filter((_, i) => i !== idx));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const anyEmpty = selectedServices.some(s => !s.product);
    if (anyEmpty) {
      notify.warn('Merci de sélectionner un produit');
      return;
    }

    const stateToSave = {
      selectedServices: normalizeSelectedServices(selectedServices),
      duo: (selectedServices.length === 2 || selectedServices.length === 4) && isActive,
      room: isRoom ? 'HOTEL_ROOM' : 'SPA_ROOM',
    };
    sessionStorage.setItem('state', JSON.stringify(stateToSave));
    console.log({ state })

    // Diagnostic
    console.debug('submit debug', { duo_capability, isActive, selectedCount: selectedServices.length });

    if (
      (selectedServices.length === 2 || selectedServices.length === 4) &&
      !hasModalShown && ((duo_capability && !isActive) || !duo_capability)
    ) {
      openModalservice();
      setHasModalShown(true);
      return false;
    }

    navigate('/choose-flypros', { state: stateToSave });
  };

  const toggleDescription = (type, id, personIndex) => {
    const updated = [...selectedServices];
    const currentlySelected = updated[personIndex].showDescriptionForId === id;

    if (currentlySelected) {
      updated[personIndex].showDescriptionForId = null;
      updated[personIndex].showDescriptionForType = null;
      setSelectedServices(updated);
      return;
    }

    updated[personIndex].showDescriptionForId = id;
    updated[personIndex].showDescriptionForType = type;
    setSelectedServices(updated);
  };

  const handleGoBackClick = (personIndex) => {
    const updated = [...selectedServices];

    const current = updated[personIndex];

    if (current.subCategory) {
      updated[personIndex] = {
        ...current,
        subCategory: null,
        product: null,
        showProductsForId: null,
        products: [],
      };
      setSelectedServices(updated);
      return;
    }
    if (current.category) {
      updated[personIndex] = {
        ...current,
        category: null,
        product: null,
        products: [],
        showProductsForId: null,
        subCategory: null,
        sub_categories: [],
      };
      setSelectedServices(updated);
      return;
    }
    if (current.domain) {
      updated[personIndex] = {
        domain: null,
        category: null,
        subCategory: null,
        product: null,
        cat_has_products: false,
        categories: [],
        sub_categories: [],
        products: [],
        sub_cat_has_products: false,
        cat_has_subcategories: false,
        showDescriptionForId: null,
        showDescriptionForType: null,
        showProductsForId: null,
        showProductsForType: null
      };
      setSelectedServices(updated);
      return;
    }
    return;
  };

  return (
    <>
      <MenuHeader />
      <Nav page="service" />
      <div className={`container container-location container-service row-${selectedServices.length}`}>
        {selectedServices[0]?.domain && selectedServices.length === 1 ? (
          <Container sx={{ display: 'flex', gap: '18%', flexWrap: 'wrap', padding: '10px 0 !important' }}>
            <ThemeProvider theme={theme}>
              <Button variant="outlined" onClick={() => handleGoBackClick(0)} color='dark' type="button"
                sx={{ textTransform: 'none', borderRadius: '15px', width: '100%', maxWidth: '50px', minHeight: '40px' }}>
                <img className="https-app" src={goback} alt="Info" />
              </Button>
            </ThemeProvider>
            <h2 className='h-goback'>{t('choose_service_type')}</h2>
          </Container>
        ) : (
          <h2>{t('choose_service_type')}</h2>
        )}

        <form className='service-form' onSubmit={handleSubmit}>
          <div className='content-row'>
            {translatedSelectedServices.map((person, idx) => {
              const translatedPerson = translatedSelectedServices[idx];
              return (
                <div key={idx} className="person-services">
                  {selectedServices.length > 1 && (
                    <h3 className='person-title'>
                      <span>
                        {selectedServices.length > 1 && selectedServices[idx].domain && (
                          <img className="https-app" src={goback} alt="Info" onClick={() => handleGoBackClick(idx)} />
                        )}
                        <img className="people" src={people} alt="icon" />
                        {idx === 0 ? t('me') : `${t('guest')} ${idx}`}
                      </span>
                      {idx > 0 && (
                        <button
                          type='button'
                          className='person-remove'
                          onClick={() => removePerson(idx)}
                        >
                          <img className='remove' src={remove} alt={t('remove')} />
                        </button>
                      )}
                    </h3>
                  )}

                  {/* show domain */}
                  {(!person?.domain || (person?.domain && !person?.categories?.length)) && (
                    <div className="services-container">
                      {translatedDomains.map(dom => {
                        const isSelected = person.domain?.id === dom.id;
                        return (
                          <div key={dom.id} className="service-wrapper">
                            <div
                              className={`service-card ${isSelected ? 'selected' : ''}`}
                              style={{
                                background: `linear-gradient(135deg, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.4)), url(${dom?.media[0]?.original_url}) no-repeat center center / cover`,
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'center center',
                                backgroundSize: 'cover',
                              }}
                            >
                              <div className="service-info">
                                <div className="service-text">
                                  <h3>{dom.title}</h3>
                                  {dom.description !== null && dom.description !== '' && (
                                    <ThemeProvider theme={theme}>
                                      <Button variant="text" color='white' onClick={() => toggleDescription("domain", dom.id, idx)} sx={{ opacity: 0.7, gap: '5px', padding: 0 }}>{t('more_info')} <img src="assets/images/icon-expand.svg" /></Button>
                                    </ThemeProvider>
                                  )}
                                </div>
                                {isSelected ? (
                                  <IconButton onClick={() => handleDomainSelect(idx, dom)}>
                                    <img src='assets/images/check.png' height={20} width={20} />
                                  </IconButton>
                                ) : (
                                  <ThemeProvider theme={theme}>
                                    <Button variant="outlined" color='white' onClick={() => handleDomainSelect(idx, dom)} sx={{ borderRadius: '15px' }}>{t('select')}</Button>
                                  </ThemeProvider>
                                )}
                              </div>
                            </div>
                            {person.showDescriptionForType === "domain" && person.showDescriptionForId === dom.id && <div className="service-description">{dom.description}</div>}
                          </div>
                        )
                      })}
                    </div>
                  )}

                  {/* show category */}
                  <div className="services-container">
                    {person.domain &&
                      Array.isArray(person.categories) &&
                      person.categories.length > 0 &&
                      person.categories
                        .map(cat => {
                          const isSelected = person.category?.id === cat.id;
                          return person.domain && (!person.category || (person.category && !person.sub_categories.length) || (person.category && person.cat_has_products && (!person.cat_has_subcategories || (person.cat_has_subcategories && person.category.id === cat.id)))) && (
                            <div key={cat.id} className="service-wrapper">
                              <div
                                className={`service-card ${isSelected ? 'selected' : ''}`}
                                style={{
                                  background: `linear-gradient(135deg, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.5)), url(${cat?.media[0]?.original_url}) no-repeat center center / cover`,
                                }}
                              >
                                <div className="service-info">
                                  <div className="service-text">
                                    <h3>{cat.title}</h3>
                                    {cat.description !== null && cat.description !== '' && (
                                      <ThemeProvider theme={theme}>
                                        <Button variant="text" color='white' onClick={() => toggleDescription("category", cat.id, idx)} sx={{ opacity: 0.7, gap: '5px', padding: 0 }}>{t('more_info')} <img src="assets/images/icon-expand.svg" /></Button>
                                      </ThemeProvider>
                                    )}
                                  </div>
                                  {isSelected ? (
                                    <IconButton onClick={() => handleCategorySelect(idx, cat)}>
                                      <img src='assets/images/check.png' height={20} width={20} />
                                    </IconButton>
                                  ) : (
                                    <ThemeProvider theme={theme}>
                                      <Button variant="outlined" color='white' onClick={() => handleCategorySelect(idx, cat)} sx={{ borderRadius: '15px' }}>{t('select')}</Button>
                                    </ThemeProvider>
                                  )}
                                </div>
                              </div>
                              {person.showDescriptionForType === "category" && person.showDescriptionForId === cat.id && <div className="service-description">{cat.description}</div>}
                              {person.showProductsForType === "category" && person.showProductsForId === cat.id && <div className='form-duree yoo'>
                                <div className="form-group serv-form">
                                  <div className="btn-group group-3" role="group">
                                    {(() => {
                                      // Trouver le produit avec le plus de réservations
                                      const mostBookedProduct = person.products.reduce((max, product) =>
                                        (product.bookings?.length || 0) > (max.bookings?.length || 0) ? product : max,
                                        person.products[0]
                                      );

                                      return person.products.map(product => (
                                        <button
                                          type="button"
                                          key={product.id}
                                          className={`btn-default 
          ${selectedServices[idx]?.product?.id === product.id ? 'btn-checked' : 'btn-unchecked'} 
          ${product.id === mostBookedProduct.id ? 'product-popular' : ''}`}
                                          onClick={() => handleProductSelect(idx, product)}
                                        >
                                          <span>{product.duration} min</span>
                                          <br />
                                          <span className="price">{product.price} chf</span>
                                          {/* {product.id === mostBookedProduct.id &&
                                          // <div className='popular-product'></div>
                                        } */}
                                        </button>
                                      ));
                                    })()}
                                  </div>
                                </div>
                              </div>
                              }{isSelected && person.showProductsForIdParent === cat.id && <div className='form-duree'>
                                <div className="form-group serv-form">
                                  <div className="btn-group group-3" role="group">
                                    {(() => {
                                      // Trouver le produit avec le plus de réservations dans productsCat
                                      const mostBookedProduct = person.productsCat.reduce((max, product) =>
                                        (product.bookings?.length || 0) > (max.bookings?.length || 0) ? product : max,
                                        person.productsCat[0]
                                      );

                                      return person.productsCat.map(product => (
                                        <button
                                          type="button"
                                          key={product.id}
                                          className={`btn-default 
          ${selectedServices[idx]?.product?.id === product.id ? 'btn-checked' : 'btn-unchecked'} 
          ${product.id === mostBookedProduct.id ? 'product-popular' : ''}`}
                                          onClick={() => handleProductSelect(idx, product)}
                                        >
                                          <span>{product.duration} min</span>
                                          <br />
                                          <span className="price">{product.price} chf</span>
                                          {/* {product.id === mostBookedProduct.id &&
                                          // <div className='popular-product'></div>
                                        } */}
                                        </button>
                                      ));
                                    })()}
                                  </div>
                                </div>
                              </div>
                              }
                            </div>
                          );
                        })
                    }
                  </div>

                  {/* show subCategories */}
                  {person?.domain && person?.category && Array.isArray(person.sub_categories) && person.sub_categories.length > 0 &&
                    (!person?.subCategory ||
                      (person?.subCategory && (!Array.isArray(person?.products) || person?.products.length === 0)) ||
                      (person?.subCategory && person?.sub_cat_has_products)
                    ) && (
                      <div className="services-container">
                        {person.sub_categories.map(sc => {
                          const isSelected = person.subCategory?.id === sc.id;
                          return (
                            <div key={sc.id} className="service-wrapper">
                              <div
                                className={`service-card ${isSelected ? 'selected' : ''}`}
                                style={{
                                  background: `linear-gradient(135deg, rgba(0,0,0,0.8), rgba(0,0,0,0.5)), url(${sc?.media?.[0]?.original_url}) center/cover no-repeat`
                                }}
                              >
                                <div className="service-info">
                                  <div className="service-text">
                                    <h3>{sc.title}</h3>
                                    {sc.description !== null && sc.description !== '' && (
                                      <ThemeProvider theme={theme}>
                                        <Button variant="text" color='white' onClick={() => toggleDescription("subCategory", sc.id, idx)} sx={{ opacity: 0.7, gap: '5px', padding: 0 }}>{t('more_info')} <img src="assets/images/icon-expand.svg" /></Button>
                                      </ThemeProvider>
                                    )}
                                  </div>
                                  {isSelected ? (
                                    <IconButton onClick={() => handleSubCategorySelect(idx, sc)}>
                                      <img src='assets/images/check.png' height={20} width={20} />
                                    </IconButton>
                                  ) : (
                                    <ThemeProvider theme={theme}>
                                      <Button variant="outlined" color='white' onClick={() => handleSubCategorySelect(idx, sc)} sx={{ borderRadius: '15px' }}>{t('select')}</Button>
                                    </ThemeProvider>
                                  )}
                                </div>
                              </div>
                              {person.showDescriptionForType === "subCategory" && person.showDescriptionForId === sc.id && <div className="service-description">{sc.description}</div>}
                              {person.showProductsForType === "subCategory" && person.showProductsForId === sc.id && (
                                <div className='form-duree'>
                                  <div className="form-group serv-form">
                                    <div className="btn-group group-3" role="group">
                                      {(() => {
                                        // PATCH: Always use array fallback!
                                        const prods = Array.isArray(person.products) ? person.products : [];
                                        if (prods.length === 0) return null;
                                        const mostBookedProduct = prods.reduce((max, product) =>
                                          (product.bookings?.length || 0) > (max.bookings?.length || 0) ? product : max,
                                          prods[0]
                                        );
                                        return prods.map(product => (
                                          <button
                                            type="button"
                                            key={product.id}
                                            className={`btn-default 
                                              ${selectedServices[idx]?.product?.id === product.id ? 'btn-checked' : 'btn-unchecked'} 
                                              ${product.id === mostBookedProduct.id ? 'product-popular' : ''}`}
                                            onClick={() => handleProductSelect(idx, product)}
                                          >
                                            <span>{product.duration} min</span>
                                            <br />
                                            <span className="price">{product.price} chf</span>
                                            {/* {product.id === mostBookedProduct.id &&
                                              <div className='popular-product'></div>
                                            } */}
                                          </button>
                                        ));
                                      })()}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                </div>
              );
            })}
          </div>

          <div className='salle'>
            <div className='info'>
              {t('salle_massage')} <i className="fa fa-info-circle"></i>
            </div>
            <div className='toggle'>
              <div
                className={`toggle-switch ${!isRoom ? 'active' : ''}`}
                onClick={toggleRoomSwitch}
              />
            </div>
            <div className='info'>
              {t('chambre_massage')} <i className="fa fa-info-circle"></i>
            </div>
            <div className='toggle'>
              <div
                className={`toggle-switch ${isRoom ? 'active' : ''}`}
                onClick={toggleRoomSwitch}
              />
            </div>
          </div>

          {(selectedServices.length === 2 || selectedServices.length === 4) && duo_capability && (
            <div className='duo'>
              <div className='info'>
                {t('duo_massage')} <i className="fa fa-info-circle"></i>
              </div>
              <div className='toggle'>
                <div
                  className={`toggle-switch ${isActive ? 'active' : ''}`}
                  onClick={toggleSwitch}
                />
              </div>
            </div>
          )}

          <Container sx={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap', padding: '0 !important' }}>
            <ThemeProvider theme={theme}>
              <Button variant="outlined" color='dark' type="button"
                onClick={addPerson} sx={{ textTransform: 'none', borderRadius: '15px', width: '100%', maxWidth: '315px', minHeight: '57px' }}>{t('add_person')}</Button>
              <Button variant="contained" color='dark_yellow' type="submit" sx={{ textTransform: 'none', borderRadius: '15px', width: '100%', maxWidth: '315px', minHeight: '57px' }}>{t('next')}</Button>
            </ThemeProvider>
          </Container>
        </form>
      </div>

      <Modal
        isOpen={modalIsOpenService}
        onRequestClose={closeModalservice}
        style={customStyles}
        contentLabel="Example Modal"
      >
        <div className="zone-not-served-container">
          <div className="popup-box">
            <button onClick={closeModalservice} className="close-btn"><img src={btnInfo} alt="Fermer" /></button>
            <p>
              {duo_capability && !isActive ? (
                t('modal_message')
              ) : !duo_capability ? (
                i18n.language && i18n.language.startsWith('fr')
                  ? "Pour votre information, il s'agit de soins à la suite avec un(e) seul(e) flypro."
                  : "For your information, these are back-to-back treatments with a single flypro."
              ) : null}
            </p>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default ChooseProducts;